import * as React from "react";
import { ChevronRight, Folder, FolderOpen, FileText, FolderPlus, Edit, Trash2 } from "lucide-react";
import { useDroppable } from "@dnd-kit/core";
import { ContextMenu as ContextMenuPrimitive } from "radix-ui";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

export type FolderNode = {
  path: string;
  name: string;
  depth: number;
  children: FolderNode[];
  documentCount: number;
};

type FolderTreeNodeProps = {
  node: FolderNode;
  selectedPath: string | null;
  expandedPaths: Set<string>;
  onToggle: (path: string) => void;
  onSelect: (path: string) => void;
  onNewDocument?: (folderPath: string) => void;
  onNewFolder?: (parentPath: string) => void;
  onRenameFolder?: (folderPath: string, currentName: string) => void;
  onDeleteFolder?: (folderPath: string, folderName: string) => void;
};

export function FolderTreeNode({
  node,
  selectedPath,
  expandedPaths,
  onToggle,
  onSelect,
  onNewDocument,
  onNewFolder,
  onRenameFolder,
  onDeleteFolder,
}: FolderTreeNodeProps) {
  const isExpanded = expandedPaths.has(node.path);
  const isSelected = selectedPath === node.path;
  const hasChildren = node.children.length > 0;

  const { setNodeRef, isOver } = useDroppable({
    id: `folder-${node.path}`,
    data: {
      folderPath: node.path,
      folderName: node.name,
    },
  });

  const handleToggle = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (hasChildren) {
        onToggle(node.path);
      }
    },
    [hasChildren, node.path, onToggle],
  );

  const handleSelect = React.useCallback(() => {
    onSelect(node.path);
  }, [node.path, onSelect]);

  const handleDoubleClick = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onSelect(node.path);
      if (hasChildren) {
        onToggle(node.path);
      }
    },
    [hasChildren, node.path, onSelect, onToggle],
  );

  const handleNewDocument = React.useCallback(() => {
    onNewDocument?.(node.path);
  }, [node.path, onNewDocument]);

  const handleNewFolder = React.useCallback(() => {
    onNewFolder?.(node.path);
  }, [node.path, onNewFolder]);

  const handleRename = React.useCallback(() => {
    onRenameFolder?.(node.path, node.name);
  }, [node.name, node.path, onRenameFolder]);

  const handleDelete = React.useCallback(() => {
    onDeleteFolder?.(node.path, node.name);
  }, [node.name, node.path, onDeleteFolder]);

  return (
    <div>
      <ContextMenuPrimitive.Root>
        <ContextMenuPrimitive.Trigger asChild>
          <div
            ref={setNodeRef}
            className={cn(
              "flex cursor-pointer items-center gap-1 rounded-md px-2 py-1.5 text-sm hover:bg-accent",
              isSelected && "bg-accent font-medium",
              isOver && "bg-primary/10 ring-2 ring-primary",
            )}
            onClick={handleSelect}
            onDoubleClick={handleDoubleClick}
            style={{ paddingLeft: `${node.depth * 12 + 8}px` }}
          >
            {hasChildren ? (
              <button
                onClick={handleToggle}
                className="flex size-4 items-center justify-center rounded hover:bg-accent-foreground/10"
                aria-label={isExpanded ? "Collapse folder" : "Expand folder"}
              >
                <ChevronRight
                  className={cn(
                    "size-3 transition-transform",
                    isExpanded && "rotate-90",
                  )}
                />
              </button>
            ) : (
              <span className="size-4" />
            )}

            {isExpanded ? (
              <FolderOpen className="size-4 text-amber-600" />
            ) : (
              <Folder className="size-4 text-amber-600" />
            )}

            <span className="flex-1 truncate">{node.name}</span>

            {node.documentCount > 0 ? (
              <Badge
                variant="outline"
                className="ml-auto shrink-0 border-zinc-300 bg-zinc-100 px-1.5 py-0 text-xs text-zinc-700"
              >
                {node.documentCount}
              </Badge>
            ) : null}
          </div>
        </ContextMenuPrimitive.Trigger>

        <ContextMenuPrimitive.Portal>
          <ContextMenuPrimitive.Content
            className="bg-popover text-popover-foreground z-50 min-w-[8rem] overflow-hidden rounded-md border p-1 shadow-md animate-in fade-in-0 zoom-in-95"
          >
            <ContextMenuPrimitive.Item
              className="focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
              onSelect={handleNewDocument}
            >
              <FileText className="size-4" />
              New Document
            </ContextMenuPrimitive.Item>
            <ContextMenuPrimitive.Item
              className="focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
              onSelect={handleNewFolder}
            >
              <FolderPlus className="size-4" />
              New Folder
            </ContextMenuPrimitive.Item>
            <ContextMenuPrimitive.Separator className="bg-border -mx-1 my-1 h-px" />
            <ContextMenuPrimitive.Item
              className="focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
              onSelect={handleRename}
            >
              <Edit className="size-4" />
              Rename
            </ContextMenuPrimitive.Item>
            <ContextMenuPrimitive.Item
              className="text-destructive focus:bg-destructive/10 focus:text-destructive [&_svg]:!text-destructive relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
              onSelect={handleDelete}
            >
              <Trash2 className="size-4" />
              Delete
            </ContextMenuPrimitive.Item>
          </ContextMenuPrimitive.Content>
        </ContextMenuPrimitive.Portal>
      </ContextMenuPrimitive.Root>

      {isExpanded && hasChildren ? (
        <div>
          {node.children.map((child) => (
            <FolderTreeNode
              key={child.path}
              node={child}
              selectedPath={selectedPath}
              expandedPaths={expandedPaths}
              onToggle={onToggle}
              onSelect={onSelect}
              onNewDocument={onNewDocument}
              onNewFolder={onNewFolder}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
